<template>
    <operational-area
        :show="{
            ...show,
            screen: loading
        }"
        :screen="{
            settings: {
                toggles: {
                    request: show.request
                },
                loading: true
            }
        }"
        :init="initSupervise"
        :closeAction="closeSupervise"
    >
        <template #operational__area >
            <div class="supervise">
                <transition name="fade" mode="out-in">
                    <div v-if="show.calls" class="supervise__call">
                        <h2 class="supervise__title color--green capitalize">{{ $t('global.callsInProgress') }}</h2>
                        <base-card class="supervise__table" :add-margins="false">
                            <template #card__body >
                                <base-table
                                    class="supervise__table__content"
                                    :headers="[
                                        'operator',
                                        'recipient',
                                        'contact',
                                        'campaign',
                                        'state'
                                    ]"
                                    :list="list"
                                    model="request"
                                    :settings="{
                                        component: 'RowRequests',
                                        removeMargin: true,
                                        maxHeight: '665px',
                                        warning: $t('global.callsInProgress')
                                    }"
                                    @intercept-call="initCall"
                                />
                            </template>
                        </base-card>
                    </div>
                    <call-area v-else :settings="{ data, call, workflow: 'intercept' }" @show-calls="showCalls" />
                </transition>
            </div>
        </template>
    </operational-area>
</template>

<script>
// packages
import { reactive, toRefs } from '@vue/composition-api';

// sockets
import initConnection from '@/plugins/sockets';

// jssip
import { logout } from '@/plugins/jssip';

// requests
import { get } from '@/helpers/requests';

export default {
    components: {
        OperationalArea: () => import( /* webpackChunkName: 'OperationalArea' */ '@/components/bundles/layout/operationalArea/OperationalArea' ),
        BaseTable: () => import( /* webpackChunkName: 'BaseTable' */ '@/components/bundles/tables/BaseTable' ),
        BaseCard: () => import( /* webpackChunkName: 'BaseCard' */ '@/components/bundles/cards/BaseCard' ),
        CallArea: () => import( /* webpackChunkName: 'CallArea' */ '@/components/bundles/layout/operationalArea/blocks/CallArea' )
    },
    setup ( _, { emit }) {
        // definição dos campos dinâmicos
        const state = reactive({
            socket: null,
            show: {
                operationalArea: false,
                iconClose: false,
                request: false,
                calls: true
            },
            loading: true,
            list: [],
            data: {},
            call: {}
        });

        /**
         * Iniciação da área de supervisão.
         * Aqui irá ser processido a conecção a API
         * através de sockets e a ligação ao freeswitch
         * através das credenciais do utilizador.
         *
         * @since 1.1.0
         * @category Função
         * @autor Tiago Martinho
         *
        */
        const initSupervise = () => {
            // toggle de secções.
            state.show.operationalArea = true;
            state.show.iconClose = true;
            state.show.request = true;
            state.loading = false;

            // iniciação do socket
            const { socket } = initConnection();

            // adicionado globalmente
            state.socket = socket;

            // iniciação da escuta do canal de chamadas activas.
            state.socket.private( 'network.self-global' )
                .listen( '.inbound.contact.calls', ({ data }) => {
                    // validação se o uiid já está listagem.
                    const request = state.list.find( ({ uuid }) => uuid === data.uuid );

                    if ( request ) {
                        // caso exista, valida se o estado é diferente
                        // se for, é actualizado o estado na tabela.
                        if ( request.state !== data.state )
                            request.state = data.state;

                        return;
                    }

                    // adicionado a listagem os novos pedidos.
                    state.list = [ {
                        id: data.id,
                        uuid: data.uuid,
                        operator: data.user.name,
                        recipient: data.contact_request.request?.person.name,
                        contact: data.destination,
                        campaign: data.campaign?.name,
                        state: data.state,
                        extension: data.extension,
                        isListening: false
                    }, ...state.list ];
                })
                .listen( '.inactive.contact.calls', ({ data }) => {
                    state.list = state.list.filter( ({ uiid }) => uiid !== data.uiid );
                });
        };

        /**
         * Fechar o painel de supervisão.
         * É procedido a desconecção do websocket,
         * de forma a que não receba mais dados.
         *
         * @since 1.1.0
         * @category Função
         * @autor Tiago Martinho
         *
        */
        const closeSupervise = () => {
            // se ainda não tiver iniciado o socket
            // não é necessário fazer disconnet. Isto pode
            // acontecer quando dá erro no registo do utilizador
            // com o freeswitch.
            if ( state.socket )
                state.socket.disconnect();

            logout();

            emit( 'close' );
        };

        /**
         * Iniciação da intercepção da chamada.
         * É procedido o pedido a API para obter os
         * detelhes da chamada ( detalhes do cliente e guião ).
         * Gerado o número para qual tem que ligar.
         *
         * @param {Object} call
         * @since 1.1.0
         * @category Função
         * @autor Tiago Martinho
         *
        */
        const initCall = async call => {
            // toggle de secções
            state.show.iconClose = false;
            state.show.operationalArea = false;
            setTimeout( () => {
                state.loading = true;
            }, 600 );

            // procedido o pedido a API para obter os dados da chamada.
            state.data = await get( 'contacts/queue', { contactCallId: call.id })
                .then( ({ data }) => data.content.data );

            state.call = { id: call.id, number: `0000${call.extension}` };

            // toggle de secções
            state.show.calls = false;
            state.loading = false;
            state.show.operationalArea = true;
        };

        /**
         * Apresentação da tabela das chamadas activas
         * aquando é terminado uma intercepção.
         *
         * @since 1.1.0
         * @category Função
         * @autor Tiago Martinho
         *
        */
        const showCalls = () => {
            state.show.calls = true;
            state.show.iconClose = true;
        };

        return {
            ...toRefs( state ),
            initSupervise,
            closeSupervise,
            initCall,
            showCalls
        };
    }
};
</script>

<style lang="scss">
    // =============================================================================
    // Animations
    // =============================================================================

    // =============================================================================
    // Style
    // =============================================================================
    .supervise {
        max-width: 1680px;
        padding: 0 20px 0;
        margin: 0 auto;
        &__title {
            @include typography-specs( 35px, 46px, 700 );
            padding-top: 80px
        }
        &__table {
            margin-top: 99px;
            &__content {
                height: 700px;
            }
        }
    }

</style>